import useStatusModal, { CLAIM_ACTION } from '../useStatusModal';
import useTranslation from '../useTranslation';
import {
    type DroneResponse,
    DroneResponseStatus,
    DroneStatusUpdateRequestStatus
} from 'generatedTypes/asset/models';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { hectaresToAcres } from '@@utils/areaUtil';
import { formatToUSDate } from '@@utils/dateTimeUtils/dateFormatUtil';
import { formatDurationSeconds } from '@@utils/dateTimeUtils/durationUtil';

import type { Props as DetailsViewEntityLinkProps } from 'components/DetailsViewEntityLink/types';
import type { BoundaryPartData, Geometry } from 'components/Map/types';

import useApplications from 'hooks/useApplications';
import type { ApplicationResponse } from 'hooks/useApplications/types';
import useBoundary from 'hooks/useBoundary';
import useDrone from 'hooks/useDrone';
import useIsAdmin from 'hooks/useIsAdmin';
import usePageRoutes from 'hooks/usePageRoutes';

import {
    DETAIL_TYPE_CONTROLLER_SERIAL_NUMBER,
    DETAIL_TYPE_DRONE_SERIAL_NUMBER,
    DETAIL_TYPE_FAA_REGISTRATION_NUMBER,
    DETAIL_TYPE_FLIGHT_CONTROLLER_FIRMWARE_VERSION,
    DETAIL_TYPE_FLIGHT_HOURS,
    DETAIL_TYPE_NUMBER_OF_FLIGHTS,
    DETAIL_TYPE_STATUS,
    DETAIL_TYPE_TOTAL_ACRES,
    DETAIL_TYPE_WARRANTY_END_DATE,
    DETAIL_TYPE_WARRANTY_REMAINING,
    DETAIL_TYPE_WARRANTY_START_DATE
} from 'pages/DroneDetailsPage/constants';

import { MS_PER_DAY } from './constants';

const useData = () => {
    const { droneId } = useParams();
    const { CLAIM_OWNERSHIP, DISABLE, EDIT_DRONE, ENABLE, RELEASE_OWNERSHIP } = useTranslation();
    const { fetchDrone, isLoading, updateDroneStatus } = useDrone();
    const { isAdmin } = useIsAdmin();
    const { assetsPage } = usePageRoutes();

    const {
        applications,
        fetch: fetchApplications,
        isLoading: isApplicationsLoading
    } = useApplications();
    const { fetchBoundary } = useBoundary();
    const navigate = useNavigate();
    const [currentDrone, setCurrentDrone] = useState<DroneResponse | undefined>(undefined);
    const [isApplicationsOpen, setIsApplicationsOpen] = useState(false);
    const [isLinkedEntitiesOpen, setIsLinkedEntitiesOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = useState<string | undefined>(
        undefined
    );
    const [selectedBoundaryGeometry, setSelectedBoundaryGeometry] = useState<Geometry | undefined>(
        undefined
    );
    const isFirstLoad = useRef(true);

    const getWarrantyDaysRemaining = () => {
        if (!currentDrone?.warranty?.end) {
            return '';
        }

        const daysRemaining = Math.ceil(
            (new Date(currentDrone.warranty.end).getTime() - Date.now()) / MS_PER_DAY
        );

        if (daysRemaining <= 0) {
            return 'Expired';
        }

        return `${daysRemaining} days`;
    };

    const entitiesLink: DetailsViewEntityLinkProps[] = [
        {
            entityName: currentDrone?.faaRegistrationNumber ?? '',
            entityType: DETAIL_TYPE_FAA_REGISTRATION_NUMBER
        },
        {
            entityName: currentDrone?.flightSeconds
                ? formatDurationSeconds(currentDrone.flightSeconds)
                : '',
            entityType: DETAIL_TYPE_FLIGHT_HOURS
        },
        {
            entityName: String(currentDrone?.numberOfFlights ?? ''),
            entityType: DETAIL_TYPE_NUMBER_OF_FLIGHTS
        },
        {
            entityName: currentDrone?.status ?? '',
            entityType: DETAIL_TYPE_STATUS
        },
        {
            entityName: currentDrone?.flightControllerSerialNumber ?? '',
            entityType: DETAIL_TYPE_DRONE_SERIAL_NUMBER
        },
        {
            entityName: currentDrone?.flightControllerSerialNumber ?? '',
            entityType: DETAIL_TYPE_CONTROLLER_SERIAL_NUMBER
        },
        {
            entityName: currentDrone?.flownHectares
                ? `${hectaresToAcres(currentDrone.flownHectares)}`
                : '',
            entityType: DETAIL_TYPE_TOTAL_ACRES
        },
        {
            entityName: currentDrone?.flightControllerFirmwareVersion ?? '',
            entityType: DETAIL_TYPE_FLIGHT_CONTROLLER_FIRMWARE_VERSION
        },
        {
            entityName: currentDrone?.warranty?.start
                ? formatToUSDate(new Date(currentDrone.warranty.start))
                : '',
            entityType: DETAIL_TYPE_WARRANTY_START_DATE
        },
        {
            entityName: currentDrone?.warranty?.end
                ? formatToUSDate(new Date(currentDrone.warranty.end))
                : '',
            entityType: DETAIL_TYPE_WARRANTY_END_DATE
        },
        {
            entityName: getWarrantyDaysRemaining(),
            entityType: DETAIL_TYPE_WARRANTY_REMAINING
        }
    ];

    const getCurrentDrone = async (id: string) => {
        if (id) {
            const drone = await fetchDrone({ id });

            setCurrentDrone(drone ?? undefined);
            fetchApplications({ droneId: id }).next();
        }
    };

    useEffect(() => {
        if (droneId) {
            getCurrentDrone(droneId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [droneId]);

    const handleSelectApplication = async (application: ApplicationResponse) => {
        setSelectedApplicationId(application.id);

        if (!application.boundaryId) {
            setSelectedBoundaryGeometry(undefined);

            return;
        }

        const boundary = await fetchBoundary({ id: application.boundaryId });

        setSelectedBoundaryGeometry(
            boundary
                ? { isEditable: false, parts: boundary.boundaryParts as BoundaryPartData[] }
                : undefined
        );
    };

    useEffect(() => {
        if (applications && applications.length > 0 && isFirstLoad.current) {
            isFirstLoad.current = false;
            handleSelectApplication(applications[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applications]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleToggleApplications = () => {
        setIsApplicationsOpen(previous => !previous);
    };

    const handleToggleLinkedEntities = () => {
        setIsLinkedEntitiesOpen(previous => !previous);
    };

    const handleEditClose = () => {
        if (isEditing) {
            setIsEditing(false);
            setIsClosing(false);

            if (droneId) {
                getCurrentDrone(droneId);
            }
        }
    };

    const handleUpdateDroneStatus = async (status: DroneStatusUpdateRequestStatus) => {
        if (!droneId) {
            return;
        }

        await updateDroneStatus({ id: droneId, status: { status } });
        getCurrentDrone(droneId);
    };

    const { handleModalClose, handleModalConfirm, modalConfig, openModal } = useStatusModal({
        onConfirm: handleUpdateDroneStatus
    });

    const editItem = { onClick: handleEditClick, text: EDIT_DRONE };

    const statusMenuItems: Partial<
        Record<DroneResponseStatus, Array<{ onClick: VoidFunction; text: string }>>
    > = {
        [DroneResponseStatus.DISABLED]: [
            {
                onClick: () => openModal(DroneStatusUpdateRequestStatus.RELEASED),
                text: RELEASE_OWNERSHIP
            },
            {
                onClick: () => openModal(DroneStatusUpdateRequestStatus.ENABLED),
                text: ENABLE
            }
        ],
        [DroneResponseStatus.ENABLED]: [
            {
                onClick: () => openModal(DroneStatusUpdateRequestStatus.RELEASED),
                text: RELEASE_OWNERSHIP
            },
            {
                onClick: () => openModal(DroneStatusUpdateRequestStatus.DISABLED),
                text: DISABLE
            }
        ],
        [DroneResponseStatus.RELEASED]: [
            {
                onClick: () => openModal(CLAIM_ACTION),
                text: CLAIM_OWNERSHIP
            }
        ]
    };

    const droneStatus = currentDrone?.status;
    const statusItems = isAdmin && droneStatus ? (statusMenuItems[droneStatus] ?? []) : [];

    const dropdownMenuItems = [editItem, ...statusItems];

    const handleNavigateBack = () => {
        navigate(assetsPage);
    };

    const handleClose = () => {
        if (isEditing) {
            setIsClosing(true);
        } else {
            handleNavigateBack();
        }
    };

    return {
        applications,
        currentDrone,
        dropdownMenuItems,
        entitiesLink,
        handleClose,
        handleEditClose,
        handleModalClose,
        handleModalConfirm,
        handleSelectApplication,
        handleToggleApplications,
        handleToggleLinkedEntities,
        isApplicationsLoading,
        isApplicationsOpen,
        isClosing,
        isEditing,
        isLinkedEntitiesOpen,
        isLoading,
        modalConfig,
        selectedApplicationId,
        selectedBoundaryGeometry,
        setIsClosing
    };
};

export default useData;
